import * as Sentry from '@sentry/react';
import { REDACTED, SECRET_QUERY_PARAMS, scrubSecretUrl, urlCarriesSecret } from '@/lib/secretUrl';

// Public DSN - safe to expose in client code
const SENTRY_DSN = 'https://8178989f8f16cab517950f6e5c38a1b3@o4510479268708352.ingest.us.sentry.io/4510479273230336';

/**
 * Error message patterns that Sentry drops before reporting (noise filter).
 * Exported so the patterns can be unit-tested independently of Sentry.init.
 */
export const IGNORED_ERROR_PATTERNS: (string | RegExp)[] = [
  'ResizeObserver loop limit exceeded',
  'ResizeObserver loop completed with undelivered notifications',
  'Non-Error promise rejection captured',
  /^Loading chunk \d+ failed/,
  // Instagram/Android WebView internal error — not actionable
  'Java object is gone',
  // Facebook/Instagram iOS in-app browser injected bridge code — not our code, not actionable
  /webkit\.messageHandlers/,
];

/** Anything Flowcut actually ships as code: a real script resource. */
const SCRIPT_FILE_RE = /\.(?:[mc]?jsx?|tsx?)(?:[?#]|$)/i;

/** The shape of a Sentry error event this module needs — kept minimal so the
 *  predicate can be unit-tested without building a full `ErrorEvent`. */
type StackBearingEvent = {
  exception?: { values?: { stacktrace?: { frames?: { filename?: string }[] } }[] };
};

/**
 * True when an exception's whole stack sits outside any script file — the
 * signature of code injected into the page by an in-app browser, never of ours.
 *
 * iOS attributes code injected through WKWebView `evaluateJavaScript` to the URL
 * of the DOCUMENT, not to a file. Flowcut code always carries a real script
 * filename instead (`/assets/*.js` chunks, `/gtm-loader.js`, `/app-bootstrap.js`,
 * `/src/*.tsx` under the dev server), and `index.html` sets a CSP with neither
 * `unsafe-inline` nor `unsafe-eval`, so our own code CANNOT produce a stack
 * attributed to the document. One first-party frame anywhere in the stack is
 * therefore enough to keep the event.
 *
 * Born from Sentry issue JAVASCRIPT-REACT-55 (2026-08-18): `RangeError: Maximum
 * call stack size exceeded.` on `/book/94d7c124-…`, iPhone / iOS 26.6, inside the
 * Google app's in-app browser. All 50 frames pointed at
 * `https://flowcutai.com/book/<uuid>` lines 187 to 224, while the HTML served for
 * that page is 79 lines long and our bundle is 24 — see
 * docs/features/infra-plateforme/2026-08-19-sentry-pile-injectee-navigateur-integre.md.
 *
 * ⚠️ Do NOT replace this with a message pattern in IGNORED_ERROR_PATTERNS:
 * "Maximum call stack size exceeded" would also silence a real stack overflow in
 * our own code. The discriminator is the absence of any script file, not the text.
 *
 * Exported pure so the invariant can be unit-tested independently of Sentry.init.
 */
export const isInjectedThirdPartyError = (event: StackBearingEvent): boolean => {
  const frames = (event.exception?.values ?? []).flatMap(
    (value) => value.stacktrace?.frames ?? [],
  );
  // No stack to judge (cross-origin "Script error.", captureMessage, non-Error
  // rejections): keep the event rather than guess.
  if (frames.length === 0) return false;
  return frames.every((frame) => !frame.filename || !SCRIPT_FILE_RE.test(frame.filename));
};

/**
 * Sampling rates, exported pure so the quota invariants can be unit-tested
 * independently of Sentry.init.
 *
 * Session replay: the free plan grants 50 replays/month. At 10% of sessions they
 * were burned in 3.5 days (51 replays between 2026-07-18 and 2026-07-21, then
 * zero), after which the ingest endpoint answered 429 — visible in the browser
 * console on /book, pointing at the addIntegration call below. Hence 0 on
 * session and 1.0 on error: the browser buffers in memory and only uploads when
 * an error actually fires.
 *
 * Traces: `beforeSend` drops errors in DEV but NEVER transactions. Without
 * tracesSampleRate at 0, every `npm run dev` shipped one span per Vite module —
 * 1.99M spans over 30 days, 77% of total usage, spent on localhost noise.
 */
export const sentrySamplingConfig = (isDev: boolean) => ({
  tracesSampleRate: isDev ? 0 : 0.2,
  replaysSessionSampleRate: 0,
  replaysOnErrorSampleRate: 1.0,
});

/** Loopback hosts, plus the Android emulator's alias for the host machine. */
const LOCAL_HOSTNAMES = new Set(['localhost', '127.0.0.1', '::1', '[::1]', '10.0.2.2']);

/**
 * True when the page is served by a Vite server on this machine.
 *
 * `import.meta.env.DEV` only tells apart the Vite DEV SERVER: it is false for
 * every build, `vite preview` included. A preview build therefore reported
 * itself as `environment: production` and slipped past `beforeSend` — see
 * Sentry issue JAVASCRIPT-REACT-50, 12 events tagged production from
 * `http://10.0.2.2:4173/fr` (Android emulator → host, `vite preview` port).
 *
 * ⚠️ The mobile app legitimately runs ON localhost and IS production:
 * `capacitor://localhost` on iOS, `https://localhost` on Android. Matching the
 * hostname alone would silence real crashes on both apps. The discriminator is
 * the PORT — a packaged app and the production website never carry one.
 *
 * Exported pure so the invariant can be unit-tested independently of Sentry.init.
 */
export const isLocalRuntime = (
  loc: Pick<Location, 'protocol' | 'hostname' | 'port'>,
): boolean => {
  if (loc.protocol === 'capacitor:') return false;
  if (!loc.port) return false;
  return LOCAL_HOSTNAMES.has(loc.hostname);
};

type Loose = Record<string, unknown>;

const isObject = (value: unknown): value is Loose => typeof value === 'object' && value !== null;

/** Masque, dans `obj`, chaque clé de `keys` dont la valeur est une chaîne (mute `obj`). */
const scrubStringKeys = (obj: unknown, keys: readonly string[]): void => {
  if (!isObject(obj)) return;
  for (const key of keys) {
    const value = obj[key];
    if (typeof value === 'string') obj[key] = scrubSecretUrl(value);
  }
};

/** Description d'un span : « GET /pay/abc » garde son verbe, le reste est une adresse. */
const scrubSpanDescription = (description: string): string => {
  const verb = /^([A-Z]+) (\S.*)$/.exec(description);
  return verb ? `${verb[1]} ${scrubSecretUrl(verb[2])}` : scrubSecretUrl(description);
};

/** `query_string` de Sentry : chaîne, objet ou liste de paires. */
const scrubQueryString = (query: unknown): unknown => {
  if (typeof query === 'string') {
    const hasMark = query.startsWith('?');
    const scrubbed = scrubSecretUrl(hasMark ? query : `?${query}`);
    return hasMark ? scrubbed : scrubbed.slice(1);
  }
  const secret = (name: unknown) =>
    typeof name === 'string' && (SECRET_QUERY_PARAMS as readonly string[]).includes(name.toLowerCase());
  if (Array.isArray(query)) {
    return query.map((pair) =>
      Array.isArray(pair) && secret(pair[0]) ? [pair[0], REDACTED, ...pair.slice(2)] : pair,
    );
  }
  if (isObject(query)) {
    for (const name of Object.keys(query)) {
      if (secret(name)) query[name] = REDACTED;
    }
  }
  return query;
};

// `transaction` : les spans autonomes (INP) portent le chemin de la page dans cet attribut.
const URL_DATA_KEYS = ['url', 'from', 'to', 'http.url', 'url.full', 'transaction'] as const;
// Le traçage fetch/xhr écrit la requête et le fragment à part de l'adresse
// (@sentry/core fetch.js:292-295 et utils/url.js:144-147).
const QUERY_DATA_KEYS = ['http.query', 'url.query'] as const;
const FRAGMENT_DATA_KEYS = ['http.fragment', 'url.fragment'] as const;

/** Masque les clés d'adresse, de requête et de fragment d'un objet `data` (mute). */
const scrubSpanData = (data: unknown): void => {
  if (!isObject(data)) return;
  scrubStringKeys(data, URL_DATA_KEYS);
  for (const key of QUERY_DATA_KEYS) {
    if (typeof data[key] === 'string') data[key] = scrubQueryString(data[key]);
  }
  for (const key of FRAGMENT_DATA_KEYS) {
    const value = data[key];
    if (typeof value !== 'string') continue;
    data[key] = value.startsWith('#') ? scrubSecretUrl(value) : scrubSecretUrl(`#${value}`).slice(1);
  }
};

/**
 * Masque les adresses noyées dans un texte libre : sous-chaînes `http(s)://...` ou chemins
 * commençant par `/` (en début de texte ou après espace, guillemet, parenthèse), jusqu'au
 * prochain blanc ou guillemet. Tout le reste est rendu octet pour octet.
 */
const scrubFreeText = (text: string): string =>
  text.replace(
    /(^|[\s"'(])(https?:\/\/[^\s"'<>)]+|\/[^\s"'<>)]*)/g,
    (_match, lead: string, url: string) => lead + scrubSecretUrl(url),
  );

const scrubBreadcrumb = (crumb: unknown): void => {
  if (!isObject(crumb)) return;
  scrubSpanData(crumb.data);
  if (typeof crumb.message === 'string') crumb.message = scrubFreeText(crumb.message);
  // Fil d'Ariane console : les arguments du journal peuvent contenir une adresse.
  if (isObject(crumb.data) && Array.isArray(crumb.data.arguments)) {
    crumb.data.arguments = crumb.data.arguments.map((arg: unknown) =>
      typeof arg === 'string' ? scrubFreeText(arg) : arg,
    );
  }
};

/**
 * Span envoyé seul (par exemple la mesure INP, que `browserTracingIntegration` émet hors
 * transaction) : ne passe que par `beforeSendSpan`, jamais par `beforeSendTransaction`.
 * Mute et rend le span (le SDK exige un span en retour). Ne lève jamais.
 */
export function scrubSentrySpan<T>(span: T): T {
  try {
    if (!isObject(span)) return span;
    const s = span as Loose;
    if (typeof s.description === 'string') s.description = scrubSpanDescription(s.description);
    scrubSpanData(s.data);
  } catch {
    // voir scrubSentryEvent
  }
  return span;
}

/**
 * Le replay enregistre le DOM et l'adresse sans passer par aucun des filtres ci-dessus :
 * on ne le charge donc pas sur une page à jeton. Une session qui ATTERRIT sur une telle
 * page n'a aucun replay, même si elle navigue ensuite ailleurs.
 */
export const canRecordReplay = (loc: Pick<Location, 'pathname' | 'search'>): boolean =>
  !urlCarriesSecret(loc.pathname, loc.search);

const scrubBreadcrumbList = (breadcrumbs: unknown): void => {
  const list = Array.isArray(breadcrumbs)
    ? breadcrumbs
    : isObject(breadcrumbs) && Array.isArray(breadcrumbs.values)
      ? breadcrumbs.values
      : [];
  for (const crumb of list) scrubBreadcrumb(crumb);
};

/**
 * Masque les jetons d'adresse d'un événement Sentry (erreur ou transaction) : mute et
 * rend l'événement. Ne lève jamais et n'écarte jamais l'événement.
 *
 * Champs (noms relevés dans `@sentry/core`, types `Event`, `Request`, `Breadcrumb`,
 * `SpanJSON`) : `request.url`, `request.query_string`, `request.headers.Referer` et
 * `referer`, `transaction`, `breadcrumbs[].data.{url,from,to}` (liste directe ou
 * `{ values }`), `spans[].description`, `spans[].data.{url,http.url,url.full}` et la
 * même chose sur `contexts.trace.data`.
 */
export function scrubSentryEvent<T>(event: T): T {
  try {
    if (!isObject(event)) return event;
    const e = event as Loose;

    if (typeof e.transaction === 'string') e.transaction = scrubSecretUrl(e.transaction);
    if (typeof e.message === 'string') e.message = scrubFreeText(e.message);
    if (isObject(e.exception) && Array.isArray(e.exception.values)) {
      for (const exc of e.exception.values) {
        if (isObject(exc) && typeof exc.value === 'string') exc.value = scrubFreeText(exc.value);
      }
    }

    if (isObject(e.request)) {
      scrubStringKeys(e.request, ['url']);
      if ('query_string' in e.request) e.request.query_string = scrubQueryString(e.request.query_string);
      scrubStringKeys(e.request.headers, ['Referer', 'referer']);
    }

    scrubBreadcrumbList(e.breadcrumbs);

    if (Array.isArray(e.spans)) {
      for (const span of e.spans) {
        if (!isObject(span)) continue;
        if (typeof span.description === 'string') span.description = scrubSpanDescription(span.description);
        scrubSpanData(span.data);
      }
    }

    if (isObject(e.contexts) && isObject(e.contexts.trace)) {
      scrubSpanData(e.contexts.trace.data);
    }
  } catch {
    // Un nettoyage raté ne doit jamais faire perdre l'événement.
  }
  return event;
}

/** Même masquage, à la source, pour un fil d'Ariane (`data.url|from|to`). Ne lève jamais. */
export function scrubSentryBreadcrumb<T>(breadcrumb: T): T {
  try {
    scrubBreadcrumb(breadcrumb);
  } catch {
    // voir scrubSentryEvent
  }
  return breadcrumb;
}

export const initSentry = () => {
  console.log('[Sentry] Initializing...');

  // Anything served by a local Vite server — dev server OR preview build — is
  // treated as development, so localhost noise never lands in production data.
  const isDev =
    import.meta.env.DEV ||
    (typeof window !== 'undefined' && isLocalRuntime(window.location));

  // Initialize with minimal integrations first for faster page load
  Sentry.init({
    dsn: SENTRY_DSN,
    environment: isDev ? 'development' : 'production',

    // Send default PII as recommended by Sentry
    sendDefaultPii: true,

    // Performance monitoring + session replay quotas (see sentrySamplingConfig)
    ...sentrySamplingConfig(isDev),

    // Filter out noise
    ignoreErrors: IGNORED_ERROR_PATTERNS,
    
    // Don't send sensitive auth data
    beforeSend(event) {
      // Don't transmit local-dev / HMR-transient errors to Sentry (localhost noise).
      if (isDev) return null;
      // Drop code injected by in-app browsers: its stack carries no script file.
      if (isInjectedThirdPartyError(event)) return null;
      // Scrub authorization headers
      if (event.request?.headers) {
        delete event.request.headers['Authorization'];
      }
      // Jetons d'adresse (réinitialisation, invitation, paiement, confirmation)
      return scrubSentryEvent(event);
    },
    beforeBreadcrumb: (breadcrumb) => scrubSentryBreadcrumb(breadcrumb),
    beforeSendTransaction: (event) => scrubSentryEvent(event),
    // Spans autonomes (INP) : elles ne passent pas par beforeSendTransaction.
    beforeSendSpan: (span) => scrubSentrySpan(span),

    integrations: [
      Sentry.browserTracingIntegration(),
      // Replay integration loaded lazily below
    ],
  });
  
  console.log('[Sentry] Initialized successfully');

  // Lazy load replay integration after page is interactive
  // This reduces initial JS parse time and unused code on landing page
  if (typeof window !== 'undefined' && !isDev && canRecordReplay(window.location)) {
    const loadReplay = () => {
      // Le replay enregistre l'adresse et le DOM hors de tout filtre : pas de replay si la
      // page porte un jeton, à l'arrivée comme au moment du chargement différé. Une session
      // qui ATTERRIT sur une page à jeton n'a donc aucun replay.
      if (!canRecordReplay(window.location)) return;
      Sentry.addIntegration(
        Sentry.replayIntegration({
          maskAllText: true,
          blockAllMedia: true,
        })
      );
    };

    // Use requestIdleCallback for non-critical loading, fallback to setTimeout
    if ('requestIdleCallback' in window) {
      requestIdleCallback(loadReplay, { timeout: 5000 });
    } else {
      setTimeout(loadReplay, 3000);
    }
  }
};

export const captureException = (error: Error, context?: Record<string, unknown>) => {
  Sentry.captureException(error, { extra: context });
  // Always log in development
  if (import.meta.env.DEV) {
    console.error('[Error captured]', error, context);
  }
};

export const setUser = (userId: string | null, email?: string) => {
  if (userId) {
    Sentry.setUser({ id: userId, email });
  } else {
    Sentry.setUser(null);
  }
};

// Export Sentry for direct access in console testing
export { Sentry };
